import { useCallback } from 'react';
import { certificatesApi } from '@/api/certificates';
import { useResource } from '@/hooks/useResource';
import type {
  Certificate,
  CertificateFilters,
  CertificateParsedDetailsResponse,
  MonitorSummaryResponse,
} from '@/types/certificate';

/**
 * Parameters for importing a certificate from a Plesk node
 */
export interface ImportCertificateParams {
  domainId: string;
  nodeId?: string;
  /** Parent Plesk subscription, if the domain lives under another one */
  pleskDomain?: string;
}

/**
 * Certificate list + certificate operations
 */
export function useCertificates(filters: CertificateFilters = {}) {
  const { status, expiring_soon, domain_id, node_id } = filters;

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await certificatesApi.getAll(
        { status, expiring_soon, domain_id, node_id },
        { signal }
      );
      return response.certificates ?? [];
    },
    [status, expiring_soon, domain_id, node_id]
  );

  const resource = useResource<Certificate[]>(fetcher, {
    errorMessage: 'Failed to load certificates',
  });
  const { setData, reload } = resource;

  const issueCertificate = useCallback(
    async (domainId: string) => {
      const result = await certificatesApi.issue({ domain_id: domainId });
      await reload();
      return result;
    },
    [reload]
  );

  /**
   * Create a certificate record for a Plesk-hosted domain and try to sync its
   * content right away. A failed sync is not fatal: the record exists and can
   * be synced later from the table.
   */
  const importCertificate = useCallback(
    async ({ domainId, nodeId, pleskDomain }: ImportCertificateParams) => {
      const result = await certificatesApi.create({ domain_id: domainId, node_id: nodeId });
      try {
        await certificatesApi.sync(
          result.certificate.id,
          pleskDomain ? { plesk_domain: pleskDomain } : undefined
        );
      } catch {
        // Sync may fail but the record is created - user can retry sync later
      }
      await reload();
      return result;
    },
    [reload]
  );

  const renewCertificate = useCallback(
    async (id: string) => {
      const result = await certificatesApi.renew(id);
      await reload();
      return result;
    },
    [reload]
  );

  const retryCertificate = useCallback(
    async (id: string) => {
      const result = await certificatesApi.retry(id);
      await reload();
      return result;
    },
    [reload]
  );

  const syncCertificate = useCallback(
    async (id: string) => {
      const result = await certificatesApi.sync(id);
      await reload();
      return result;
    },
    [reload]
  );

  const deleteCertificate = useCallback(
    async (id: string): Promise<void> => {
      await certificatesApi.delete(id);
      setData((prev) => prev?.filter((cert) => cert.id !== id));
    },
    [setData]
  );

  /**
   * Check all certificates on their source nodes for updates
   */
  const runMonitoring = useCallback(async (): Promise<MonitorSummaryResponse> => {
    const result = await certificatesApi.triggerMonitoring();
    if (result.updated > 0) {
      await reload();
    }
    return result;
  }, [reload]);

  return {
    certificates: resource.data ?? [],
    isLoading: resource.isLoading,
    error: resource.error,
    reload,
    issueCertificate,
    importCertificate,
    renewCertificate,
    retryCertificate,
    syncCertificate,
    deleteCertificate,
    runMonitoring,
  };
}

/**
 * Parsed X.509 details of one certificate (pass null to disable)
 */
export function useCertificateDetails(certificateId: string | null) {
  const fetcher = useCallback(
    (signal: AbortSignal) => certificatesApi.getParsedDetails(certificateId as string, { signal }),
    [certificateId]
  );

  const resource = useResource<CertificateParsedDetailsResponse>(certificateId ? fetcher : null, {
    errorMessage: 'Failed to load certificate details',
    keepPreviousData: false,
  });

  return {
    details: resource.data ?? null,
    isLoading: resource.isLoading,
    error: resource.error,
  };
}
